import React from 'react';
import { useSelector } from 'react-redux';
import { getCatalogCoverUrl } from '../pages/CatalogViewPage';
import { useTranslation } from 'react-i18next';

const CatalogAboutModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const user = useSelector((state) => state.app.user.value);
    const catalog = useSelector((state) => state.app.catalog.value);

    if (!isOpen) return null;

    const catalogCoverUrl = getCatalogCoverUrl(catalog);

    const isValidString = (val) => Boolean(val && typeof val === 'string' && val.trim() !== '' && val !== 'null' && val !== 'undefined');

    // Strict Enabled flags from User document in Firestore
    const isEmailEnabled = (user?.enableEmail === true || user?.enableEmail === undefined) && isValidString(user?.correo);
    
    // Format Phone for WhatsApp
    const countryCode = user?.cpp ? `${user.cpp}` : '';
    const phoneNum = user?.telefono ? `${user.telefono}` : '';
    const fullPhone = `${countryCode}${phoneNum}`.replace(/[^0-9]/g, '');

    // WhatsApp enable flag (wp boolean or enableWp boolean)
    const isWhatsappEnabled = (user?.wp === true || user?.enableWp === true) && Boolean(fullPhone);

    const whatsappUrl = isWhatsappEnabled 
        ? `https://api.whatsapp.com/send?phone=${fullPhone}&text=${encodeURIComponent(`¡Hola ${user?.nombre || ''}! Vi tu catálogo "${catalog?.name || ''}" y quisiera solicitar información.`)}` 
        : null;

    // Instagram enabled if handle exists and enableIg is not false
    const isInstagramEnabled = user?.enableIg !== false && isValidString(user?.ig_user);
    const instagramUrl = (isInstagramEnabled && user?.ig_user)
        ? (user.ig_user.startsWith('http') ? user.ig_user : `https://instagram.com/${user.ig_user.replace('@', '')}`)
        : null;

    // Facebook enabled if handle exists and enableFb is not false
    const isFacebookEnabled = user?.enableFb !== false && isValidString(user?.fb_user);
    const facebookUrl = (isFacebookEnabled && user?.fb_user)
        ? (user.fb_user.startsWith('http') ? user.fb_user : `https://facebook.com/${user.fb_user}`)
        : null;

    const hasAnyContactMethod = isEmailEnabled || isWhatsappEnabled || isInstagramEnabled || isFacebookEnabled;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content rounded-16 border-0 overflow-hidden shadow-lg">
                    {/* Header */}
                    <div className="modal-header bg-main-600 text-white p-20 border-0 flex-between">
                        <div className="d-flex align-items-center gap-12">
                            <i className="ph ph-info text-2xl"></i>
                            <h5 className="modal-title text-white fw-bold mb-0">{t('vendorAbout.aboutVendor')}</h5>
                        </div>
                        <button 
                            type="button" 
                            className="btn-close btn-close-white" 
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body p-24">
                        {/* Catalog & Vendor Info Header */}
                        <div className="text-center mb-24 pb-24 border-bottom">
                            {catalogCoverUrl || user?.logoUrl ? (
                                <img 
                                    src={catalogCoverUrl || user?.logoUrl} 
                                    alt={catalog?.name || user?.nombre} 
                                    className="w-80 h-80 rounded-circle object-fit-cover mx-auto mb-12 border border-2 shadow-xs"
                                />
                            ) : (
                                <div className="w-80 h-80 rounded-circle bg-main-100 text-main-600 flex-center text-3xl fw-bold mx-auto mb-12 shadow-xs">
                                    {(catalog?.name || user?.nombre || 'V')[0]}
                                </div>
                            )}

                            <h4 className="fw-bold text-gray-900 mb-4">{catalog?.name || t('vendorAbout.catalogDetails')}</h4>
                            <p className="text-gray-500 text-sm mb-8">{catalog?.description || ''}</p>
                            
                            {user?.nombre && (
                                <div 
                                    className="d-inline-block bg-main-50 text-main-600 rounded-16 px-16 py-8 text-xs fw-bold text-wrap text-break" 
                                    style={{ maxWidth: '250px', lineHeight: '1.4' }}
                                >
                                    <i className="ph ph-storefront me-4"></i> {t('vendorAbout.aboutVendor')}: {user.nombre}
                                </div>
                            )}
                        </div>

                        {/* Direct Contact Details */}
                        {(isEmailEnabled || isWhatsappEnabled) && (
                            <>
                                <h6 className="fw-bold text-gray-800 text-xs text-uppercase mb-16">{t('vendorAbout.contact')}</h6>

                                <div className="d-flex flex-column gap-12 mb-24">
                                    {isEmailEnabled && (
                                        <div className="d-flex align-items-center gap-12 p-12 rounded-12 bg-gray-50 border">
                                            <div className="w-36 h-36 rounded-circle bg-main-100 text-main-600 flex-center flex-shrink-0">
                                                <i className="ph ph-envelope text-lg"></i>
                                            </div>
                                            <div className="overflow-hidden">
                                                <span className="text-2xs text-gray-400 d-block">{t('vendorAbout.email')}</span>
                                                <a href={`mailto:${user.correo}`} className="text-sm fw-bold text-gray-800 hover-text-main-600 text-truncate d-block">
                                                    {user.correo}
                                                </a>
                                            </div>
                                        </div>
                                    )}

                                    {isWhatsappEnabled && (
                                        <div className="d-flex align-items-center gap-12 p-12 rounded-12 bg-gray-50 border">
                                            <div className="w-36 h-36 rounded-circle bg-success-100 text-success-600 flex-center flex-shrink-0">
                                                <i className="ph ph-phone text-lg"></i>
                                            </div>
                                            <div>
                                                <span className="text-2xs text-gray-400 d-block">{t('vendorAbout.phone')}</span>
                                                <span className="text-sm fw-bold text-gray-800">
                                                    +{countryCode} {phoneNum}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Social Media & Instant Messaging Buttons */}
                        {(whatsappUrl || instagramUrl || facebookUrl) && (
                            <>
                                <h6 className="fw-bold text-gray-800 text-xs text-uppercase mb-12">{t('vendorAbout.socialMedia')}</h6>
                                <div className="d-grid gap-10">
                                    {whatsappUrl && (
                                        <a 
                                            href={whatsappUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="btn btn-success rounded-pill py-10 flex-center gap-8 fw-bold text-sm"
                                        >
                                            <i className="ph ph-whatsapp-logo text-xl"></i>
                                            WhatsApp
                                        </a>
                                    )}

                                    {instagramUrl && (
                                        <a 
                                            href={instagramUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="btn text-white rounded-pill py-10 flex-center gap-8 fw-bold text-sm"
                                            style={{ backgroundColor: '#E1306C' }}
                                        >
                                            <i className="ph ph-instagram-logo text-xl"></i>
                                            Instagram
                                        </a>
                                    )}

                                    {facebookUrl && (
                                        <a 
                                            href={facebookUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="btn btn-primary rounded-pill py-10 flex-center gap-8 fw-bold text-sm"
                                            style={{ backgroundColor: '#1877F2' }}
                                        >
                                            <i className="ph ph-facebook-logo text-xl"></i>
                                            Facebook
                                        </a>
                                    )}
                                </div>
                            </>
                        )}

                        {!hasAnyContactMethod && (
                            <p className="text-xs text-gray-400 text-center mb-0 py-16">
                                {t('vendorAbout.noContactInfo', { defaultValue: 'No contact information available.' })}
                            </p>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer border-top p-16">
                        <button type="button" className="btn btn-gray rounded-pill w-100 py-10 text-xs fw-bold" onClick={onClose}>
                            {t('vendorAbout.close')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CatalogAboutModal;
